import { Icon, TextLink } from "@components/library";
import { COLORS, FONTS } from "@constants";
import { useSSOSensitiveEmail } from "@hooks/useSSOSensitiveEmail";
import haloLogo from "@images/logos/halo_logo.svg";
import { RootState } from "@redux/store";
import { marketplace_path } from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { LoginModalPreviousAction } from "@tsTypes/redux";
import { popSignupPrefillEmailCookie } from "@utils/authUtils";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { LoginSignUpModalType } from "../LoginSignUpModal";
import SignUpModalDefaultForm from "./SignUpModalDefaultForm";
import SignUpModalSSOForm from "./SignUpModalSSOForm";
import TEXTS from "./signUpModalTexts";

interface Props {
  setModalType: Dispatch<SetStateAction<LoginSignUpModalType>>;
}

/**
 * Figma: https://cutt.ly/uilibrary-sign-up-modal
 */
const SignUpModal = ({ setModalType }: Props) => {
  const [shouldShowSSO, setShouldShowSSO] = useState(false);
  const { email, setEmail, isEmailSSOEnabled, redirectToSSO } = useSSOSensitiveEmail(
    popSignupPrefillEmailCookie()
  );

  useEffect(() => {
    if (isEmailSSOEnabled) setShouldShowSSO(true);
  }, [isEmailSSOEnabled]);

  const previousAction = useSelector(
    (state: RootState) => state.profiles.loginModalState.previousAction
  );

  const { pathname } = useLocation();

  let texts = TEXTS.default;
  if (pathname.startsWith(marketplace_path())) {
    texts = TEXTS.marketplace;
  } else if (previousAction === LoginModalPreviousAction.REFINE_RECOMMENDATIONS) {
    texts = TEXTS.refineRecommendations;
  }

  return (
    <>
      <Container>
        <Link to={marketplacePath()}>
          <Logo src={haloLogo} alt="Halo logo" />
        </Link>
        <Heading>{texts.heading}</Heading>
        <Subheading>{texts.subheading}</Subheading>
        <MainSection>
          <Bullets>
            {texts.bullets.map((text) => (
              <Bullet key={text}>
                <Icon name="Checkmark Alt" color={COLORS.HALO_BLUE} size="xs" margin="4px 0 0" />
                {text}
              </Bullet>
            ))}
          </Bullets>
          <FormSection>
            {shouldShowSSO ? (
              <SignUpModalSSOForm
                setShouldShowSSO={setShouldShowSSO}
                email={email}
                setEmail={setEmail}
                isEmailSSOEnabled={isEmailSSOEnabled}
                redirectToSSO={redirectToSSO}
              />
            ) : (
              <SignUpModalDefaultForm
                setShouldShowSSO={setShouldShowSSO}
                email={email}
                setEmail={setEmail}
              />
            )}
            <Terms>
              By signing up you agree to the{" "}
              <TextLink
                to="https://knowledge.halo.science/terms-of-service"
                newTab
                underline
                color={COLORS.HALO_BLUE}
                font={FONTS.REGULAR_4}
              >
                Terms of Service
              </TextLink>
              ,{" "}
              <TextLink
                to="https://knowledge.halo.science/privacy-policy"
                newTab
                underline
                color={COLORS.HALO_BLUE}
                font={FONTS.REGULAR_4}
              >
                Privacy Policy
              </TextLink>{" "}
              and{" "}
              <TextLink
                to="https://knowledge.halo.science/community-standards"
                newTab
                underline
                color={COLORS.HALO_BLUE}
                font={FONTS.REGULAR_4}
              >
                Community Standards
              </TextLink>
              .
            </Terms>
          </FormSection>
        </MainSection>
      </Container>
      <Footer>
        Already have an account?&nbsp;
        <LinkButton type="button" onClick={() => setModalType("login")}>
          Sign in
        </LinkButton>
      </Footer>
    </>
  );
};

export default SignUpModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 48px 32px;
`;
const Logo = styled.img`
  height: 32px;
  margin-bottom: 16px;
`;
const Heading = styled.div`
  margin-bottom: 8px;
  ${FONTS.HEADING_5_REGULAR};
`;
const Subheading = styled.div`
  margin-bottom: 32px;
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_500};
`;
const MainSection = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
`;
const Bullets = styled.ul`
  all: unset;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 232px;
`;
const Bullet = styled.li`
  display: flex;
  gap: 8px;
  ${FONTS.REGULAR_2};
`;
const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 312px;
`;
const Terms = styled.div`
  ${FONTS.REGULAR_4};
  color: ${COLORS.NEUTRAL_600};
`;
const Footer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 72px;
  border-top: 1px solid ${COLORS.NEUTRAL_250};
  ${FONTS.MEDIUM_2};
`;
// Used in SignUpModalDefaultForm and SignUpModalSSOForm
export const LinkButton = styled.button`
  all: unset;
  ${FONTS.MEDIUM_2};
  color: ${COLORS.HALO_BLUE};
  text-decoration: underline;
  text-underline-position: from-font;
  cursor: pointer;
`;
